/**
 * One period control: a box reading `01-Apr-2025 - 31-Mar-2026` that opens a
 * month calendar with the preset chips beside it (docs/03 — `Period*`).
 *
 * The first click on a day starts the range and the second ends it, the way
 * the reference's picker reads. Two bare date inputs made the period two
 * questions where the business asks one.
 */
import { useEffect, useRef, useState } from 'react'
import { Icon } from '../design/index.js'
import { DATE_PRESETS, type DatePreset } from './filters.js'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const
const LONG_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const

const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** `2025-04-01` → `01-Apr-2025`, as the reference writes a date in a filter. */
export const dmy = (value: string | undefined): string => {
  if (!value) return ''
  const [y, m, d] = value.split('-')
  const month = MONTHS[Number(m) - 1]
  return y && month && d ? `${d}-${month}-${y}` : value
}

/** The reference's chip order: the financial year first, the month last. */
const ORDER = ['thisFy', 'lastFy', 'thisQuarter', 'lastQuarter', 'thisMonth', 'lastMonth'] as const
const TITLE: Readonly<Record<string, string>> = {
  thisFy: 'This FY', lastFy: 'Last FY', thisQuarter: 'This Quarter',
  lastQuarter: 'Last Quarter', thisMonth: 'This Month', lastMonth: 'Last Month',
}
export const RANGE_PRESETS: readonly DatePreset[] = ORDER
  .map((key) => DATE_PRESETS.find((p) => p.key === key))
  .filter((p): p is DatePreset => p !== undefined)
  .map((p) => ({ ...p, label: TITLE[p.key] ?? p.label }))

export const DateRangePicker = ({
  from, to, onChange, label = 'Period', disabled = false,
}: {
  from: string | undefined
  to: string | undefined
  onChange: (range: { from: string; to: string }) => void
  label?: string
  disabled?: boolean
}) => {
  const [open, setOpen] = useState(false)
  const [start, setStart] = useState<string | null>(null)
  const seed = from ? new Date(`${from}T00:00:00`) : new Date()
  const [month, setMonth] = useState({ y: seed.getFullYear(), m: seed.getMonth() })
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent): void => {
      if (box.current && !box.current.contains(e.target as Node)) { setOpen(false); setStart(null) }
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const first = new Date(month.y, month.m, 1)
  const cells = Array.from({ length: 42 }, (_, i) => new Date(month.y, month.m, i - first.getDay() + 1))
  const step = (by: number): void => {
    const d = new Date(month.y, month.m + by, 1)
    setMonth({ y: d.getFullYear(), m: d.getMonth() })
  }
  const pick = (day: string): void => {
    if (start === null) { setStart(day); return }
    onChange(day < start ? { from: day, to: start } : { from: start, to: day })
    setStart(null)
    setOpen(false)
  }
  const lo = start ?? from
  const hi = start ?? to
  const text = from || to ? `${dmy(from)} - ${dmy(to)}` : ''

  return (
    <div className="range-pick" ref={box}>
      <button type="button" className="control range-pick__box" aria-label={label} aria-expanded={open}
        disabled={disabled} onClick={() => setOpen(!open)}>
        <span>{text || 'Select a period'}</span>
        <Icon name="down" size={14} />
      </button>
      {open ? (
        <div className="range-pick__pop" role="dialog" aria-label={`${label} calendar`}>
          <div className="range-pick__cal">
            <div className="range-pick__nav">
              <span className="range-pick__month">
                <select aria-label="Month" value={month.m} onChange={(e) => setMonth({ ...month, m: Number(e.currentTarget.value) })}>
                  {LONG_MONTHS.map((name, i) => <option key={name} value={i}>{name}</option>)}
                </select>
                <input aria-label="Year" type="number" value={month.y}
                  onChange={(e) => { const y = Number(e.currentTarget.value); if (y > 1900 && y < 3000) setMonth({ ...month, y }) }} />
              </span>
              <button type="button" aria-label="Previous month" onClick={() => step(-1)}><Icon name="left" size={14} /></button>
              <button type="button" aria-label="Next month" onClick={() => step(1)}><Icon name="right" size={14} /></button>
            </div>
            <div className="range-pick__grid">
              {DAYS.map((d) => <span key={d} className="range-pick__dow">{d}</span>)}
              {cells.map((d) => {
                const day = iso(d)
                const inRange = lo !== undefined && hi !== undefined && day >= lo && day <= hi
                const edge = day === lo || day === hi
                const cls = ['range-pick__day',
                  d.getMonth() !== month.m ? 'range-pick__day--out' : '',
                  inRange ? 'range-pick__day--in' : '',
                  edge ? 'range-pick__day--edge' : ''].filter(Boolean).join(' ')
                return (
                  <button key={day} type="button" className={cls} aria-label={dmy(day)} aria-pressed={edge}
                    onClick={() => pick(day)}>{d.getDate()}</button>
                )
              })}
            </div>
          </div>
          <div className="range-pick__presets">
            {RANGE_PRESETS.map((p) => (
              <button key={p.key} type="button" className="preset"
                onClick={() => { onChange(p.range()); setStart(null); setOpen(false) }}>{p.label}</button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
